/**
 * Sistema de Critical CSS para mejorar First Contentful Paint
 * Extrae y aplica CSS crítico inline en el <head>
 */

export const criticalStyles = `
  /* Reset y Base */
  *,::before,::after{box-sizing:border-box;border-width:0;border-style:solid;border-color:#e5e7eb}
  html{line-height:1.5;-webkit-text-size-adjust:100%;font-family:ui-sans-serif,system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,"Noto Sans",sans-serif}
  body{margin:0;line-height:inherit}
  
  /* Layout Crítico */
  .min-h-screen{min-height:100vh}
  .bg-white{background-color:#fff}
  .bg-gradient-to-br{background-image:linear-gradient(to bottom right,var(--tw-gradient-stops))}
  .from-cyan-400{--tw-gradient-from:#22d3ee;--tw-gradient-stops:var(--tw-gradient-from),var(--tw-gradient-to,rgba(34,211,238,0))}
  .to-pink-500{--tw-gradient-to:#ec4899}
  
  /* Navbar Crítico */
  .fixed{position:fixed}
  .top-0{top:0}
  .left-0{left:0}
  .right-0{right:0}
  .z-50{z-index:50}
  .transition-all{transition-property:all;transition-timing-function:cubic-bezier(.4,0,.2,1);transition-duration:.15s}
  
  /* Hero Crítico */
  .pt-32{padding-top:8rem}
  .pb-20{padding-bottom:5rem}
  .text-center{text-align:center}
  .text-5xl{font-size:3rem;line-height:1}
  .font-bold{font-weight:700}
  .text-gray-900{color:#111827}
  
  /* Flexbox Crítico */
  .flex{display:flex}
  .items-center{align-items:center}
  .justify-center{justify-content:center}
  .gap-4{gap:1rem}
  
  /* Botones Críticos */
  .px-8{padding-left:2rem;padding-right:2rem}
  .py-4{padding-top:1rem;padding-bottom:1rem}
  .rounded-full{border-radius:9999px}
  .text-white{color:#fff}
  .cursor-pointer{cursor:pointer}
  
  /* Animación de Carga */
  @keyframes spin{to{transform:rotate(360deg)}}
  .animate-spin{animation:spin 1s linear infinite}
  
  /* Ocultar contenido no crítico inicialmente */
  .lazy-load{opacity:0;transition:opacity .3s ease-in}
  .lazy-load.loaded{opacity:1}
`;

/**
 * Inyecta CSS crítico en el <head>
 */
export function injectCriticalCSS(): void {
  if (typeof document === 'undefined') return;

  const existingStyle = document.getElementById('critical-css');
  if (existingStyle) return;

  const style = document.createElement('style');
  style.id = 'critical-css';
  style.textContent = criticalStyles;
  
  // Insertar antes del primer <link> o al inicio del <head>
  const firstLink = document.head.querySelector('link');
  if (firstLink) {
    document.head.insertBefore(style, firstLink);
  } else {
    document.head.appendChild(style);
  }
}

/**
 * Precarga de recursos críticos
 */
export function preloadCriticalResources(): void {
  if (typeof document === 'undefined') return;

  const resources = [
    // Google Fonts
    {
      href: 'https://fonts.googleapis.com',
      rel: 'preconnect',
      crossOrigin: 'anonymous'
    },
    {
      href: 'https://fonts.gstatic.com',
      rel: 'preconnect',
      crossOrigin: 'anonymous'
    },
    // CDN de iconos
    {
      href: 'https://cdn.jsdelivr.net',
      rel: 'preconnect',
      crossOrigin: 'anonymous'
    }
  ];

  resources.forEach(({ href, rel, crossOrigin }) => {
    const existing = document.head.querySelector(`link[href="${href}"]`);
    if (existing) return;

    const link = document.createElement('link');
    link.rel = rel;
    link.href = href;
    if (crossOrigin) {
      link.crossOrigin = crossOrigin;
    }
    document.head.appendChild(link);
  });
}

/**
 * Lazy load de imágenes con IntersectionObserver
 */
export function initLazyLoading(): void {
  if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
    return;
  }

  const imageObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        const img = entry.target as HTMLImageElement;
        const src = img.dataset.src;
        
        if (src) {
          img.src = src;
          img.classList.add('loaded');
          imageObserver.unobserve(img);
        }
      }
    });
  }, {
    rootMargin: '50px 0px',
    threshold: 0.01
  });

  // Observar todas las imágenes con data-src
  document.querySelectorAll('img[data-src]').forEach((img) => {
    imageObserver.observe(img);
  });
}

/**
 * Optimización de Web Fonts
 */
export function optimizeWebFonts(): void {
  if (typeof document === 'undefined') return;

  // Font Display Swap para evitar FOIT (Flash of Invisible Text)
  const fontLinks = document.querySelectorAll('link[href*="fonts.googleapis.com"]');
  fontLinks.forEach((link) => {
    const href = link.getAttribute('href');
    if (href && !href.includes('display=swap')) {
      link.setAttribute('href', `${href}&display=swap`);
    }
  });
}

/**
 * Inicializar todas las optimizaciones críticas
 */
export function initCriticalOptimizations(): void {
  // Inyectar CSS crítico inmediatamente
  injectCriticalCSS();
  
  // Precargar recursos críticos
  preloadCriticalResources();
  
  // Optimizar fuentes
  optimizeWebFonts();
  
  // Lazy loading cuando el DOM esté listo
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initLazyLoading);
  } else {
    initLazyLoading();
  }
}

export default {
  injectCriticalCSS,
  preloadCriticalResources,
  initLazyLoading,
  optimizeWebFonts,
  initCriticalOptimizations
};
